import { Pipe, PipeTransform } from '@angular/core';
import { BaseModel, Holding, OldPortfolio, PortfolioFlagsEnum, Result, UserPerms, WebUserDataConstants } from '@ddt-web/domain';
import * as dayjs from 'dayjs';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import * as get from 'lodash/get';
import * as isNil from 'lodash/isNil';

import { PortfolioModelUtil } from '../utils/portfolio-model.util';
import { JsonToHoldingPipe } from './json-to-holding.pipe';

dayjs.extend(customParseFormat);

@Pipe({
	name: 'jsonToPortfolio'
})
export class JsonToPortfolioPipe implements PipeTransform {
	private jsonToHoldingPipe: JsonToHoldingPipe = new JsonToHoldingPipe();

	public transform(portfolioData: Result, args?: any): any {
		if (!portfolioData?.children) {
			console.log('Portfolio has no positions: ' + portfolioData.uniqueId);
			return null;
		}

		return this.getUpdatedPortfolio(portfolioData, true, false);
	}

	public transformWithoutPosition(portfolioData: Result, args?: any): any {
		return this.getUpdatedPortfolio(portfolioData);
	}

	public transformToBaseModel(portfolioData: any, args?: any): BaseModel {
		const modelPortfolio: BaseModel = new BaseModel('', '', '', '');
		if (portfolioData?.portfolioId) {
			modelPortfolio.setUniqueId(portfolioData.portfolioId.toString());
			modelPortfolio.setType(WebUserDataConstants.TYPE.MODEL_PORTFOLIO);
		}
		if (portfolioData?.modelPortfolio?.attributesByCodeMap?.model_portfolio_long_name?.attributeTranslation) {
			modelPortfolio.setName(portfolioData.modelPortfolio.attributesByCodeMap.model_portfolio_long_name.attributeTranslation);
		}
		if (
			Array.isArray(get(portfolioData, 'modelPortfolio.analyticsByCodeMap.MPSREBAL.analyticValue')) &&
			portfolioData?.modelPortfolio?.analyticsByCodeMap?.MPSREBAL?.analyticValue?.[1]
		) {
			modelPortfolio.setLastModifiedDate(dayjs(portfolioData.modelPortfolio.analyticsByCodeMap.MPSREBAL.analyticValue[1].toString(), 'YYYYMMDD').format('MM/DD/YYYY'));
		} else if (portfolioData?.modelPortfolio?.analyticsByCodeMap?.MPSREBAL?.analyticValue) {
			modelPortfolio.setLastModifiedDate(dayjs(portfolioData.modelPortfolio.analyticsByCodeMap.MPSREBAL.analyticValue.toString(), 'YYYYMMDD').format('MM/DD/YYYY'));
		}
		return modelPortfolio;
	}

	private getUpdatedPortfolio(portfolioData: any, shouldIncludeHoldings: boolean = false, shouldLogErrors: boolean = true): any {
		const holdings: Array<Holding> = new Array<Holding>();
		const flags: PortfolioFlagsEnum[] = [];
		let errorReasons: string = 'Errors while building portfolio:\n';
		let hasErrors: boolean = false;

		// portfolio name
		let description: string = '';
		if (portfolioData?.description) {
			description = portfolioData.description;
		} else if (portfolioData?.title) {
			description = portfolioData.title;
		} else if (portfolioData?.name) {
			description = portfolioData.name;
		} else {
			description = '';
			errorReasons += 'No description provided\n';
			hasErrors = true;
		}

		// portfolio date
		let dateLastModified: number = 0;
		if (!isNil(portfolioData?.dateLastModified)) {
			dateLastModified = portfolioData.dateLastModified;
		} else {
			dateLastModified = 0;
			errorReasons += 'No last modified date\n';
			hasErrors = true;
		}

		if (shouldIncludeHoldings) {
			if (portfolioData.type === WebUserDataConstants.TYPE.PORTFOLIO || portfolioData.type === WebUserDataConstants.TYPE.PORTFOLIO_LIKE) {
				const firstWeight = get(portfolioData, 'children[0].weight');
				let equalWeights: boolean = portfolioData.children && portfolioData.children.length > 1;
				for (const holdingJson of portfolioData.children) {
					const holding: Holding = this.jsonToHoldingPipe.transform(holdingJson);
					if (holding.isErroneous()) {
						hasErrors = true;
					}
					equalWeights = equalWeights && firstWeight === holding.getWeight();
					holdings.push(holding);
				}
				if (holdings.length === 0) {
					flags.push(PortfolioFlagsEnum.NO_HOLDINGS);
				} else {
					if (!firstWeight) {
						flags.push(PortfolioFlagsEnum.NO_WEIGTHS);
					} else if (equalWeights) {
						flags.push(PortfolioFlagsEnum.EQUAL_WEIGHTS);
					}
				}
				if (portfolioData.type === WebUserDataConstants.TYPE.PORTFOLIO_LIKE) {
					flags.push(PortfolioFlagsEnum.LIST_OF_HOLDINGS);
				}
			}
		}


		// portfolio id
		let uniqueId: string = '';
		if (portfolioData?.uniqueId) {
			uniqueId = portfolioData.uniqueId;
		} else {
			uniqueId = '';
			errorReasons += 'Portfolio has no unique ID\n';
			hasErrors = true;
		}

		// portfolio type
		let type: string = '';
		if (portfolioData?.type) {
			type = portfolioData.type;
		} else {
			errorReasons += 'No type\n';
			hasErrors = true;
		}

		// portfolio subType
		let subType: string = '';
		if (portfolioData?.subType) {
			subType = portfolioData.subType;
		}

		// from this part, not sure if needed, but not very significant now, leaving it as is
		let lastModifyingUser: string = '';
		if (portfolioData.updateUserId) {
			lastModifyingUser = portfolioData.updateUserId;
		}

		let owner: string = '';
		if (portfolioData?.userId) {
			owner = portfolioData.userId;
		} else {
			owner = '';
			errorReasons += 'No owner\n';
			hasErrors = true;
		}

		let dateCreated: number = 0;
		if (!isNil(portfolioData?.dateCreated)) {
			dateCreated = portfolioData.dateCreated;
		} else if (portfolioData.type === WebUserDataConstants.TYPE.PDF && !isNil(portfolioData.asOfDate)) {
			dateCreated = portfolioData.asOfDate;
		} else {
			dateCreated = 0;
			errorReasons += 'No creation date\n';
			hasErrors = true;
		}

		let userPerms: UserPerms;
		if (portfolioData?.allowedPermsForCurrentUser) {
			userPerms = PortfolioModelUtil.getUserPerms(portfolioData);
		} else if (portfolioData?.perms) {
			userPerms = new UserPerms(Object.keys(portfolioData.perms));
		}

		let value: number = null;
		if (!isNil(portfolioData.value)) {
			value = portfolioData.value;
			flags.push(PortfolioFlagsEnum.VALUE);
		} else {
			flags.push(PortfolioFlagsEnum.NO_VALUE);
		}

		let outputDesignation: string = '';
		if (portfolioData.outputDesignation) {
			outputDesignation = portfolioData.outputDesignation;
		}

		let portfolioType: string = '';
		if (portfolioData.portfolioType) {
			portfolioType = portfolioData.portfolioType;
		}

		let portfolioKey: string = '';
		if (portfolioData.portfolioKey) {
			portfolioKey = portfolioData.portfolioKey;
		}

		const port: OldPortfolio = new OldPortfolio(
			description,
			dateLastModified,
			lastModifyingUser,
			holdings,
			uniqueId,
			type,
			subType,
			owner,
			dateCreated,
			value,
			userPerms,
			flags,
			outputDesignation,
			portfolioType,
			portfolioKey
		);

		if (portfolioData?.action) {
			port.setAction(portfolioData.action);
		}
		if (!isNil(portfolioData.asOfDate)) {
			port.setAsOfDate(portfolioData.asOfDate);
		}
		if (hasErrors) {
			if (shouldLogErrors) {
				console.error('Portfolio built with errors', port, errorReasons);
			} else {
				console.log('Portfolio built with errors: ' + port.uniqueId);
			}
		}

		return port;
	}
}
